import React from "react";
import { useLocation, Navigate } from "react-router-dom";
import { getCookieJson } from "../utils/cookie";
import { IAM_COOKIE_NAME } from "../settings/config";


type Props = {
    children: JSX.Element;
};


type Subscription = {
    status?: string;
    plan_code?: string;
}

type Organization = {
    guid?: string;
    role?: string;
}


const allowedRoutes = [
    "/subscription/plans",
    "/user/account/profile",
    "/user/account/billing",
    "/auth/logout",
    "/auth/login",
    "/free-trial-setup"
];


const shouldRedirectToPlans = (organization: Organization | null) => {
    if (!organization) return true;
    if (organization.role === "owner") return true;
    if (!organization.role || !organization.guid) return true;
    return false;
};

const routeIsForbidden = (pathname: string) => !allowedRoutes.includes(pathname) && !pathname.startsWith("/subscription/checkout/");
const shouldRedirectToProfile = (organization: Organization | null) => organization && organization.guid && organization.role === "member";
const hasExpiredSubscription = (subscription: Subscription | null) => subscription && subscription.status === "expired";

const getSubscription = (accessToken: string) => {
    try {
        const tokenBody = JSON.parse(atob(accessToken.split(".")[1]));
        return tokenBody["user_claims"]["subscription"];
    } catch {
        return null;
    }
};

const getOrganization = (accessToken: string) => {
    try {
        const tokenBody = JSON.parse(atob(accessToken.split(".")[1]));

        return {
            guid: tokenBody["user_claims"]["o_guid"],
            role: tokenBody["user_claims"]["org_role"]
        };
    } catch {
        return null;
    }
};


export default function AccessControl({ children }: Props) {
    const location = useLocation();
    const pathname = location.pathname;

    const iamCookie = getCookieJson(IAM_COOKIE_NAME);

    if (iamCookie && iamCookie["access_token"]) {
        const subscription: Subscription | null = getSubscription(iamCookie["access_token"]);
        const organization: Organization | null = getOrganization(iamCookie["access_token"]);

        if (subscription !== null && Object.keys(subscription).length === 0) {
            if (pathname !== "/free-trial-setup") {
                return <Navigate to="/free-trial-setup" replace />;
            }
        } else if (hasExpiredSubscription(subscription) && routeIsForbidden(pathname)) {
            if (shouldRedirectToPlans(organization)) {
                return <Navigate to="/subscription/plans" replace />;
            } else if (shouldRedirectToProfile(organization)) {
                return <Navigate to="/user/account/profile" replace />;
            }
        }
    }

    return children;
}